/**
 * PCM16 audio helpers used by the OpenAI Realtime adapter.
 *
 * - Float32 samples in [-1, 1] are the Web Audio native format.
 * - The OpenAI Realtime API transports audio as base64-encoded PCM16
 *   little-endian mono (we run the AudioContext at 24 kHz).
 *
 * Everything here is pure and browser-only (uses `atob` / `btoa`).
 */

/** Convert a Float32 sample array (range [-1, 1]) to clamped Int16 PCM. */
export function float32ToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    // noUncheckedIndexedAccess: guard against the `T | undefined` shape.
    const sample = input[i] ?? 0;
    const s = Math.max(-1, Math.min(1, sample));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Convert Int16 PCM back to Float32 samples in [-1, 1]. */
export function pcm16ToFloat32(input: Int16Array): Float32Array {
  const out = new Float32Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const sample = input[i] ?? 0;
    // Divide by 0x8000 so that the min value (-32768) maps cleanly to -1.
    out[i] = sample / 0x8000;
  }
  return out;
}

/** Encode Int16 PCM as a base64 string (little-endian byte order). */
export function pcm16ToBase64(input: Int16Array): string {
  const bytes = new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  // Chunk to avoid call-stack blowups on large buffers with String.fromCharCode.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, Math.min(i + CHUNK, bytes.length));
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

/** Decode a base64 string into Int16 PCM (little-endian byte order). */
export function base64ToPcm16(b64: string): Int16Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  // The byte length must be even for Int16 alignment; OpenAI always sends
  // even-length PCM16 chunks. Slice to a fresh ArrayBuffer so alignment is
  // guaranteed regardless of the source buffer offset.
  const aligned = new Uint8Array(bytes.length - (bytes.length % 2));
  aligned.set(bytes.subarray(0, aligned.length));
  return new Int16Array(aligned.buffer);
}
